import { useMemo } from 'react';
import { formatUnits, zeroAddress } from 'viem';
import { useConnection, useReadContracts } from 'wagmi';
import { StakingContract } from 'utils/constants/abi/StakingContract';
import { stakingPoolListQueryOptions } from 'structure/staking/hooks/useStakingAPI';
import { useQuery } from '@tanstack/react-query';
import { StakingPool } from '../../../../api/StakingAPI';
import { STAKING_VALUE_REFRESH_INTERVAL } from 'structure/staking/hooks/useStakingPoolValues';
import { useTokenPrices } from 'utils/hooks/useTokenPrices';
import {
  getStakingPoolTokenPrice,
  getStakingTokenPrice,
} from 'structure/staking/utils/stakingTokenPrice';

type UserAmounts = {
  pendingRewards: bigint;
  staked: bigint;
};

const ZERO = BigInt(0);

const formatTokenAmount = (value: bigint, decimals: number) =>
  Number(formatUnits(value, decimals));

const getBackendPoolTokenPrice = (pool: StakingPool) => {
  const tokensStaked = Number(pool.tokensStaked);
  const tvl = Number(pool.totalValueLockedUSD);

  return Number.isFinite(tokensStaked) &&
    tokensStaked > 0 &&
    Number.isFinite(tvl)
    ? tvl / tokensStaked
    : 0;
};

export const useUserTotalStakeAssets = () => {
  const connection = useConnection();
  const poolsQuery = useQuery({
    ...stakingPoolListQueryOptions({
      query: {
        refetchInterval: STAKING_VALUE_REFRESH_INTERVAL,
        staleTime: STAKING_VALUE_REFRESH_INTERVAL,
      },
    }),
  });
  const tokenPricesQuery = useTokenPrices({
    query: {
      refetchInterval: STAKING_VALUE_REFRESH_INTERVAL,
      staleTime: STAKING_VALUE_REFRESH_INTERVAL,
    },
  });
  const pools = useMemo(() => poolsQuery.data ?? [], [poolsQuery.data]);

  const userContracts = useMemo(
    () =>
      pools.flatMap((pool) => [
        {
          address: pool.stakingContractAddress,
          abi: StakingContract,
          functionName: 'userInfo' as const,
          args: [connection.address ?? zeroAddress] as const,
          chainId: Number(pool.chainId),
        },
        {
          address: pool.stakingContractAddress,
          abi: StakingContract,
          functionName: 'pendingFor' as const,
          args: [connection.address ?? zeroAddress] as const,
          chainId: Number(pool.chainId),
        },
      ]),
    [connection.address, pools]
  );
  const userAmountsQuery = useReadContracts({
    contracts: userContracts,
    query: {
      enabled: !!connection.address && userContracts.length > 0,
      refetchInterval: STAKING_VALUE_REFRESH_INTERVAL,
      staleTime: STAKING_VALUE_REFRESH_INTERVAL,
    },
  });

  const userAmounts = useMemo<UserAmounts[]>(
    () =>
      pools.map((_, index) => {
        const userInfoResult = userAmountsQuery.data?.[index * 2];
        const pendingResult = userAmountsQuery.data?.[index * 2 + 1];
        const userInfo =
          userInfoResult?.status === 'success'
            ? (userInfoResult.result as readonly unknown[])
            : undefined;

        return {
          staked: (userInfo?.[0] as bigint | undefined) ?? ZERO,
          pendingRewards:
            pendingResult?.status === 'success'
              ? (pendingResult.result as bigint)
              : ZERO,
        };
      }),
    [pools, userAmountsQuery.data]
  );

  const data = useMemo(() => {
    if (!connection.address) return 0;

    return pools.reduce((total, pool, index) => {
      const amounts = userAmounts[index];
      const liveDepositPrice = getStakingPoolTokenPrice(
        tokenPricesQuery.data,
        pool
      );
      const depositPrice = liveDepositPrice || getBackendPoolTokenPrice(pool);
      const liveRewardPrice = getStakingTokenPrice(
        tokenPricesQuery.data,
        Number(pool.chainId),
        pool.rewardsTokenAddress
      );
      const rewardPrice =
        liveRewardPrice ||
        (pool.rewardsTokenAddress.toLowerCase() ===
        pool.depositTokenAddress.toLowerCase()
          ? depositPrice
          : 0);
      const stakedValue =
        formatTokenAmount(amounts.staked, pool.depositTokenDecimals) *
        depositPrice;
      const rewardValue =
        formatTokenAmount(amounts.pendingRewards, pool.rewardsTokenDecimals) *
        rewardPrice;

      return total + stakedValue + rewardValue;
    }, 0);
  }, [connection.address, pools, tokenPricesQuery.data, userAmounts]);

  return {
    data,
    error:
      poolsQuery.error ?? tokenPricesQuery.error ?? userAmountsQuery.error,
    isLoading:
      poolsQuery.isLoading ||
      tokenPricesQuery.isLoading ||
      userAmountsQuery.isLoading,
    refetch: async () => {
      await Promise.all([
        poolsQuery.refetch(),
        tokenPricesQuery.refetch(),
        userAmountsQuery.refetch(),
      ]);
    },
  };
};
